import React, { useState, useEffect } from 'react';
import { obtenerEstadisticas } from '../api/backend_v2';
import { formatearMoneda } from '../utils/moneda';
import {
  ChartBarSquareIcon,
  UsersIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  CreditCardIcon,
  EyeIcon,
  PencilSquareIcon,
  SparklesIcon,
  BeakerIcon,
  InboxArrowDownIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  PhotoIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  TrophyIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';

// Genera las opciones de período disponibles
function generarOpciones() {
  const hoy = new Date();
  const anioActual = hoy.getFullYear();
  const mesActualNum = hoy.getMonth() + 1;
  const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const opciones = [
    { id: 'todos', label: 'Todo', sub: 'Sin filtro', filtro: {} },
    { id: `anio-${anioActual}`, label: `${anioActual}`, sub: 'Año completo', filtro: { anio: anioActual } },
  ];
  // Últimos 12 meses hacia atrás
  for (let i = 0; i < 12; i++) {
    let m = mesActualNum - i;
    let a = anioActual;
    if (m <= 0) { m += 12; a -= 1; }
    opciones.push({
      id: `${a}-${m}`,
      label: MESES[m - 1],
      sub: a !== anioActual ? String(a) : (i === 0 ? 'Este mes' : ''),
      filtro: { mes: m, anio: a },
      esActual: i === 0,
    });
  }
  return opciones;
}

const OPCIONES_PERIODO = generarOpciones();

const Dashboard = ({ onViewChange, usuario }) => {
  const hoy = new Date();
  const filtroInicial = { id: `${hoy.getFullYear()}-${hoy.getMonth() + 1}`, filtro: { mes: hoy.getMonth() + 1, anio: hoy.getFullYear() } };

  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState(filtroInicial.id);
  const [filtroPeriodo, setFiltroPeriodo] = useState(filtroInicial.filtro);
  const [mostrarSelectorMeses, setMostrarSelectorMeses] = useState(false);
  const puedeVerFinanzas = usuario?.rol !== 'operario';

  const opcionActual = OPCIONES_PERIODO.find(o => o.id === periodoSeleccionado) || OPCIONES_PERIODO[0];
  const labelPeriodo = opcionActual
    ? (opcionActual.sub && opcionActual.sub !== 'Sin filtro'
        ? `${opcionActual.label} ${opcionActual.sub}`.trim()
        : opcionActual.label)
    : 'Todos';

  useEffect(() => {
    cargarEstadisticas(filtroPeriodo);
  }, [filtroPeriodo]);

  const cargarEstadisticas = async (filtro) => {
    try {
      setLoading(true);
      const data = await obtenerEstadisticas(filtro);
      setEstadisticas(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const seleccionarPeriodo = (opcion) => {
    setPeriodoSeleccionado(opcion.id);
    setFiltroPeriodo(opcion.filtro);
    setMostrarSelectorMeses(false);
  };

  // Etiqueta del período para mostrar en tarjetas
  const mesActual = labelPeriodo;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  // Colores y íonos por estado
    const ESTADO_CONFIG = {
      'Tomado':    { bg: 'bg-blue-500', text: 'text-blue-700', icon: PencilSquareIcon },
      'Preparado': { bg: 'bg-yellow-500', text: 'text-yellow-700', icon: SparklesIcon },
      'En laboratorio': { bg: 'bg-purple-500', text: 'text-purple-700', icon: BeakerIcon },
      'Para retirar': { bg: 'bg-orange-500', text: 'text-orange-700', icon: InboxArrowDownIcon },
      'Entregado': { bg: 'bg-green-500', text: 'text-green-700', icon: CheckCircleIcon },
    }; 
  const ESTADOS_ORDEN = ['Tomado', 'Preparado', 'En laboratorio', 'Para retirar', 'Entregado'];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <ChartBarSquareIcon className="h-8 w-8 text-blue-600" />
              Dashboard - Óptica
            </h1>
            <p className="text-gray-600 mt-2">
              {puedeVerFinanzas
                ? 'Vista general de clientes, anteojos y finanzas'
                : 'Vista general de clientes y anteojos'}
            </p>
          </div>

          {/* Selector de período */}
          <div className="relative">
            <button
              onClick={() => setMostrarSelectorMeses(!mostrarSelectorMeses)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold transition-all hover:shadow-md"
            >
              <CalendarDaysIcon className="h-5 w-5" />
              <span>{labelPeriodo}</span>
              <span className={`text-xs transition-transform ${mostrarSelectorMeses ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {mostrarSelectorMeses && (
              <div className="absolute right-0 mt-2 z-50 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-80">
                {/* Opciones rápidas */}
                <div className="flex gap-2 mb-3">
                  {OPCIONES_PERIODO.slice(0, 2).map(op => (
                    <button
                      key={op.id}
                      onClick={() => seleccionarPeriodo(op)}
                      className={`flex-1 flex flex-col items-center py-2 px-3 rounded-xl border-2 transition-all text-sm font-semibold
                        ${ periodoSeleccionado === op.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                    >
                      <span>{op.label}</span>
                      <span className="text-xs font-normal text-gray-400">{op.sub}</span>
                    </button>
                  ))}
                </div>

                {/* Grilla de meses */}
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Mes específico</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {OPCIONES_PERIODO.slice(2).map(op => (
                    <button
                      key={op.id}
                      onClick={() => seleccionarPeriodo(op)}
                      className={`flex flex-col items-center py-2 px-1 rounded-xl border-2 transition-all
                        ${ periodoSeleccionado === op.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                          : op.esActual
                            ? 'border-blue-200 bg-blue-50/50 text-blue-600 hover:border-blue-400'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                    >
                      <span className="text-xs font-bold">{op.label}</span>
                      {op.sub ? <span className="text-[10px] text-gray-400 leading-tight">{op.sub}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Estadísticas Financieras Principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {puedeVerFinanzas && (
            <div 
              onClick={() => onViewChange('finanzas')}
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200 hover:bg-gray-50"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <BanknotesIcon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Ingresos Totales
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        ${formatearMoneda(estadisticas?.ingresos_totales)}
                      </dd>
                      <dd className="text-xs text-gray-400 mt-0.5">{mesActual}</dd>
                    </dl>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-gray-400">→</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {puedeVerFinanzas && (
            <div 
              onClick={() => onViewChange('finanzas')}
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200 hover:bg-gray-50"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                      <BuildingLibraryIcon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Saldos Pendientes
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        ${formatearMoneda(estadisticas?.saldo_pendiente)}
                      </dd>
                    </dl>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-gray-400">→</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {puedeVerFinanzas && (
            <div 
              onClick={() => onViewChange('finanzas')}
              className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200 hover:bg-gray-50"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <CreditCardIcon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Señas
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        ${formatearMoneda(estadisticas?.total_senas)}
                      </dd>
                    </dl>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-gray-400">→</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Card Total Clientes */}
          <div 
            onClick={() => onViewChange('clientes')}
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200 hover:bg-gray-50"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <UsersIcon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Clientes
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {estadisticas?.total_clientes || 0}
                    </dd>
                  </dl>
                </div>
                <div className="flex-shrink-0">
                  <span className="text-gray-400">→</span>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Estadísticas de Estados de Anteojos - Refactorizado */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {ESTADOS_ORDEN.map((estado) => {
            const config = ESTADO_CONFIG[estado];
            return (
            <div
              key={estado}
              onClick={() => onViewChange(`anteojos-${estado.toLowerCase().replace(/ /g, '-')}`)}
              className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow duration-200 hover:${config.bg}`}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 ${config.bg} rounded-md flex items-center justify-center`}>
                      <config.icon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{estado}</dt>
                      <dd className="text-lg font-medium text-gray-900">{estadisticas?.estados?.[estado] || 0}</dd>
                    </dl>
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`text-xl ${config.text}`}>→</span>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
        {/* ── Accesos directos ──────────────────────────────────────── */}
        <div className="mb-8">
          <h2 className="text-base font-semibold text-gray-500 uppercase tracking-wider mb-3">Accesos rápidos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[
              { label: 'Clientes',       icon: UsersIcon,                  view: 'clientes',            color: 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700' },
              { label: 'Todos los anteojos', icon: EyeIcon,               view: 'anteojos-todos',      color: 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700' },
              { label: 'Lista de Precios', icon: ClipboardDocumentListIcon, view: 'lista-precios',       color: 'bg-violet-50 hover:bg-violet-100 border-violet-200 text-violet-700' },
              { label: 'Stock Armazones', icon: PhotoIcon,                  view: 'stock-armazones',     color: 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700' },
              { label: 'Cristales',      icon: BeakerIcon,                  view: 'agregar-cristales',   color: 'bg-cyan-50 hover:bg-cyan-100 border-cyan-200 text-cyan-700' },
              { label: 'Mensajería',     icon: ChatBubbleLeftRightIcon,     view: 'mensajeria',          color: 'bg-green-50 hover:bg-green-100 border-green-200 text-green-700' },
              ...(puedeVerFinanzas ? [
                { label: 'Estadísticas Ventas', icon: ChartBarIcon,           view: 'estadisticas-ventas', color: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700' },
                { label: 'Finanzas',     icon: BanknotesIcon,               view: 'finanzas',            color: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700' },
                { label: 'Calendario de Pagos', icon: CalendarDaysIcon,    view: 'finanzas-calendario', color: 'bg-teal-50 hover:bg-teal-100 border-teal-200 text-teal-700' },
                { label: 'Facturación',  icon: DocumentTextIcon,           view: 'facturacion',         color: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-700' },
              ] : []),
            ].map(({ label, icon: Icon, view, color }) => (
              <button
                key={view}
                onClick={() => onViewChange(view)}
                className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all hover:scale-105 hover:shadow-md active:scale-100 ${color}`}
              >
                <Icon className="h-7 w-7" />
                <span className="text-xs font-semibold text-center leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>
        {puedeVerFinanzas && (
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2 mb-1">
              <BriefcaseIcon className="h-5 w-5 text-gray-500" />
              Resumen Financiero
            </h3>
            <p className="text-sm text-gray-400 mb-6">{mesActual}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">
                  ${formatearMoneda(estadisticas?.ingresos_totales)}
                </div>
                <div className="text-sm text-gray-600 mt-1 flex items-center justify-center gap-1">
                  <BanknotesIcon className="h-4 w-4 text-green-500" /> Ingresos Totales
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Suma de todos los trabajos realizados
                </div>
              </div>

              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-600">
                  ${formatearMoneda(estadisticas?.total_senas)}
                </div>
                <div className="text-sm text-gray-600 mt-1 flex items-center justify-center gap-1">
                  <CreditCardIcon className="h-4 w-4 text-yellow-500" /> Total de Señas
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Pagos recibidos como anticipo
                </div>
              </div>

              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-3xl font-bold text-red-600">
                  ${formatearMoneda(estadisticas?.saldo_pendiente)}
                </div>
                <div className="text-sm text-gray-600 mt-1 flex items-center justify-center gap-1">
                  <BuildingLibraryIcon className="h-4 w-4 text-red-500" /> Saldos Pendientes
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Dinero por cobrar de trabajos
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-900">
                  Eficiencia de Cobros
                </div>
                <div className="text-sm text-gray-600">
                  {estadisticas?.ingresos_totales > 0
                    ? `${Math.round(((estadisticas?.total_senas || 0) / estadisticas?.ingresos_totales) * 100)}% cobrado`
                    : '0% cobrado'
                  }
                </div>
              </div>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{
                    width: estadisticas?.ingresos_totales > 0
                      ? `${Math.round(((estadisticas?.total_senas || 0) / estadisticas?.ingresos_totales) * 100)}%`
                      : '0%'
                  }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Progreso de Estados */}
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center gap-2">
              <EyeIcon className="h-5 w-5 text-gray-500" />
              Estado de Anteojos
            </h3>
            <div className="space-y-4">
              {ESTADOS_ORDEN.map((estado) => {
                const cantidad = estadisticas?.estados?.[estado] || 0;
                const total = estadisticas?.total_anteojos || 1;
                const porcentaje = Math.round((cantidad / total) * 100);
                const config = ESTADO_CONFIG[estado];
                return (
                  <div key={estado} className="flex items-center">
                    <div className="w-32 text-sm font-medium flex items-center gap-2">
                      <config.icon className={`h-5 w-5 ${config.text}`} />
                      <span className="truncate">{estado}</span>
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${config.bg}`}
                          style={{ width: `${porcentaje}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className={`w-20 text-sm text-right font-semibold ${config.text}`}>
                      {cantidad} ({porcentaje}%)
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        {/* Resumen de actividad reciente */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-gray-500" />
              Resumen General del Negocio
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {estadisticas?.total_anteojos || 0}
                </div>
                <div className="text-sm text-gray-500">Total de Trabajos</div>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {estadisticas?.total_clientes || 0}
                </div>
                <div className="text-sm text-gray-500">Clientes Registrados</div>
              </div>
            </div>
            
            {/* Top Clientes */}
            {estadisticas?.top_clientes && estadisticas.top_clientes.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <TrophyIcon className="h-4 w-4 text-yellow-500" />
                  Clientes con Más Trabajos
                </h4>
                <div className="space-y-2">
                  {estadisticas.top_clientes.slice(0, 3).map((cliente, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">{cliente.nombre}</span>
                      <span className="text-gray-500">{cliente.total_anteojos} trabajos</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
          
          </div>
        </div>
  );
};

export default Dashboard;
